import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Compass, Clock, MapPin, ArrowRight, BellRing } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { API } from "@/lib/site";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const EmptyState = () => {
    const [email, setEmail] = useState("");
    const [saving, setSaving] = useState(false);

    const notify = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await axios.post(`${API}/contact`, {
                name: "Package notify list",
                email,
                type: "notify",
                message: "Please notify me when new tour packages are published.",
            });
            toast.success("You are on the list — thank you!");
            setEmail("");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not subscribe. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="relative overflow-hidden rounded-3xl border-2 border-brass/40 bg-white p-10 text-center shadow-[0_30px_80px_-40px_rgba(10,25,47,0.4)] sm:p-16" data-testid="packages-empty-state">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(212,175,55,0.10),transparent_60%)]" />
            <Compass className="mx-auto h-20 w-20 text-brass/50 animate-slow-spin" strokeWidth={0.75} />
            <h2 className="mt-8 font-serif text-2xl font-semibold tracking-tight text-navy sm:text-3xl lg:text-4xl">
                Signature collections under curation
            </h2>
            <div className="gold-rule mx-auto mt-6" />
            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-steel">
                Our first set of curated domestic itineraries — royal Rajasthan, Himalayan escapes, Kerala
                backwaters and more — is being crafted for the coming travel season. Leave your email and be
                the first to see them, or let us design a private journey today.
            </p>
            <form onSubmit={notify} className="mx-auto mt-10 flex max-w-md flex-col gap-3 sm:flex-row" data-testid="notify-form">
                <Input
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="input-classic flex-1"
                    data-testid="notify-email-input"
                />
                <Button
                    type="submit"
                    disabled={saving}
                    data-testid="notify-submit-button"
                    className="rounded-full bg-navy px-6 py-3 text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-navy-800"
                >
                    <span className="inline-flex items-center gap-2">
                        <BellRing className="h-4 w-4 text-brass" /> Notify Me
                    </span>
                </Button>
            </form>
            <Link
                to="/book"
                data-testid="bespoke-itinerary-button"
                className="group mt-6 inline-flex items-center gap-2 text-sm font-semibold text-navy transition-colors hover:text-brass-dark"
            >
                Request a bespoke itinerary instead
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
        </div>
    );
};

const PackageCard = ({ pkg }) => (
    <Reveal className="h-full">
        <article className="card-frame flex h-full flex-col overflow-hidden" data-testid={`package-card-${pkg.id}`}>
            {pkg.image_url ? (
                <img src={pkg.image_url} alt={pkg.title} className="h-56 w-full object-cover" />
            ) : (
                <div className="flex h-56 w-full items-center justify-center bg-navy">
                    <Compass className="h-12 w-12 text-brass/60" strokeWidth={0.75} />
                </div>
            )}
            <div className="flex flex-1 flex-col p-7">
                <h3 className="font-serif text-xl font-semibold text-navy">{pkg.title}</h3>
                <div className="mt-3 flex flex-wrap gap-4 text-xs text-steel">
                    <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-brass" /> {pkg.destination}</span>
                    <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-brass" /> {pkg.duration}</span>
                </div>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-steel">{pkg.summary}</p>
                {pkg.price ? (
                    <p className="mt-5 font-serif text-2xl font-semibold text-navy">
                        ₹{pkg.price}<span className="ml-1 text-xs font-sans font-normal text-steel">/ person</span>
                    </p>
                ) : null}
                <Link
                    to={`/book?dest=${encodeURIComponent(pkg.destination || pkg.title)}`}
                    data-testid={`package-book-button-${pkg.id}`}
                    className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-brass px-6 py-3 text-sm font-semibold text-navy transition-colors duration-300 hover:bg-brass-dark"
                >
                    Book This Package
                </Link>
            </div>
        </article>
    </Reveal>
);

const Packages = () => {
    const [packages, setPackages] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/packages`)
            .then((res) => setPackages(res.data.packages || []))
            .catch(() => setPackages([]));
    }, []);

    return (
        <>
            <PageHeader
                eyebrow="Tour Packages"
                title="Curated journeys, ready when you are"
                description="Browse our signature domestic packages — or ask for a bespoke route built entirely around you."
            />
            <section className="py-20 lg:py-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    {packages === null ? (
                        <div className="grid gap-8 md:grid-cols-3" data-testid="packages-loading">
                            {[1, 2, 3].map((n) => (
                                <div key={n} className="card-frame h-[420px] animate-pulse rounded-2xl bg-white/60" />
                            ))}
                        </div>
                    ) : packages.length === 0 ? (
                        <EmptyState />
                    ) : (
                        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="packages-grid">
                            {packages.map((pkg) => (
                                <PackageCard key={pkg.id} pkg={pkg} />
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </>
    );
};

export default Packages;
